/** Single leaf in digital book pagination (spread sides are sequential entries). */
export interface BookPage {
  type: "cover-front" | "cover-back" | "blank" | "story";
  pageNumber?: number;
  imageUrl?: string;
  isSinglePage?: boolean;
  isFullSpread?: boolean;
  accessibleDescription?: string;
}

/** One full-screen step in DigitalBookReader — cover alone, or two pages side by side. */
export type BookReaderView =
  | { kind: "single"; index: number }
  | { kind: "spread"; leftIndex: number; rightIndex: number };

/**
 * Collapse linear `pageSequence` (cover, blanks×2, story pairs, back cover)
 * into navigation steps. Story pairs in sequence are [main, side]; each spread
 * shows main on the left and side on the right (LTR). Blanks: left then right.
 */
export function buildBookReaderViews(sequence: BookPage[]): BookReaderView[] {
  const views: BookReaderView[] = [];
  let i = 0;
  const n = sequence.length;
  while (i < n) {
    const p = sequence[i];
    if (!p) break;
    if (p.type === "cover-front" || p.type === "cover-back" || p.isSinglePage === true) {
      views.push({ kind: "single", index: i });
      i += 1;
      continue;
    }
    const next = sequence[i + 1];
    if (next && p.type === "blank" && next.type === "blank") {
      views.push({ kind: "spread", leftIndex: i, rightIndex: i + 1 });
      i += 2;
      continue;
    }
    if (
      next &&
      p.type === "story" &&
      next.type === "story" &&
      typeof p.pageNumber === "number" &&
      p.pageNumber === next.pageNumber
    ) {
      views.push({ kind: "spread", leftIndex: i, rightIndex: i + 1 });
      i += 2;
      continue;
    }
    views.push({ kind: "single", index: i });
    i += 1;
  }
  return views;
}

export function bookReaderViewCount(sequence: BookPage[]): number {
  return buildBookReaderViews(sequence).length;
}

export function getBookLayoutAt(
  pageSequence: BookPage[],
  index: number
): { left: BookPage | null; right: BookPage | null; isSinglePage: boolean; isFullSpread: boolean } {
  const currentPage = pageSequence[index] || null;

  const isCover =
    currentPage &&
    (currentPage.type === "cover-front" || currentPage.type === "cover-back" || currentPage.isSinglePage);

  if (isCover) {
    return { left: null, right: currentPage, isSinglePage: true, isFullSpread: false };
  }

  /* Each blank is its own leaf in the digital reader (sequential paging). */
  if (currentPage?.type === "blank") {
    return { left: null, right: currentPage, isSinglePage: true, isFullSpread: false };
  }

  if (currentPage?.type === "story") {
    const nextPage = pageSequence[index + 1] || null;
    const prevPage = index > 0 ? pageSequence[index - 1] : null;
    const nextIsSameSpread =
      nextPage?.type === "story" && nextPage?.pageNumber === currentPage.pageNumber;
    const prevIsSameSpread =
      prevPage?.type === "story" && prevPage?.pageNumber === currentPage.pageNumber;

    if (nextIsSameSpread) {
      return {
        left: currentPage,
        right: nextPage,
        isSinglePage: false,
        isFullSpread: currentPage.isFullSpread ?? false,
      };
    }
    if (prevIsSameSpread) {
      return {
        left: prevPage,
        right: currentPage,
        isSinglePage: false,
        isFullSpread: currentPage.isFullSpread ?? false,
      };
    }
  }

  if (currentPage?.isFullSpread && currentPage.type === "story") {
    return { left: currentPage, right: currentPage, isSinglePage: false, isFullSpread: true };
  }

  const fallbackLeft = pageSequence[index] || null;
  const fallbackRight = index < pageSequence.length - 1 ? pageSequence[index + 1] : null;

  return { left: fallbackLeft, right: fallbackRight, isSinglePage: false, isFullSpread: false };
}

/** Story `pageNumber` for the reader step (spread or single story leaf), else null. */
export function getStoryPageNumberForReaderView(
  view: BookReaderView | undefined,
  sequence: BookPage[]
): number | null {
  if (!view) return null;
  if (view.kind === "single") {
    const p = sequence[view.index];
    return p?.type === "story" && typeof p.pageNumber === "number" ? p.pageNumber : null;
  }
  const left = sequence[view.leftIndex];
  if (left?.type === "story" && typeof left.pageNumber === "number") return left.pageNumber;
  const right = sequence[view.rightIndex];
  if (right?.type === "story" && typeof right.pageNumber === "number") return right.pageNumber;
  return null;
}

export function getStorySpreadPageNumber(pageSequence: BookPage[], currentPageIndex: number): number | null {
  const bookLayout = getBookLayoutAt(pageSequence, currentPageIndex);
  if (bookLayout.left?.type === "story" && typeof bookLayout.left.pageNumber === "number") {
    return bookLayout.left.pageNumber;
  }
  if (bookLayout.right?.type === "story" && typeof bookLayout.right.pageNumber === "number") {
    return bookLayout.right.pageNumber;
  }
  return null;
}

export function getNextPageSequenceIndex(pageSequence: BookPage[], idx: number): number {
  const currentPage = pageSequence[idx];
  if (idx >= pageSequence.length - 1) return idx;
  if (currentPage?.type === "story") {
    const nextPage = pageSequence[idx + 1];
    const nextIsSameSpread =
      nextPage?.type === "story" && nextPage?.pageNumber === currentPage.pageNumber;
    if (nextIsSameSpread || currentPage.isFullSpread) {
      return Math.min(idx + 2, pageSequence.length - 1);
    }
  }
  return idx + 1;
}

export function getPrevPageSequenceIndex(pageSequence: BookPage[], idx: number): number {
  const currentPage = pageSequence[idx];
  if (idx <= 0) return 0;
  if (currentPage?.type === "story") {
    const prevPage = pageSequence[idx - 1];
    const prevIsSameSpread =
      prevPage?.type === "story" && prevPage?.pageNumber === currentPage.pageNumber;
    const nextPage = pageSequence[idx + 1];
    const nextIsSameSpread =
      nextPage?.type === "story" && nextPage?.pageNumber === currentPage.pageNumber;
    if (nextIsSameSpread) {
      return Math.max(0, idx - 1);
    }
    const spreadStart = prevIsSameSpread ? idx - 1 : idx;
    return Math.max(0, spreadStart - 2);
  }
  return idx - 1;
}
